/**
 * Black Box — Event Schema (SINGLE SOURCE OF TRUTH)
 *
 * Every other workspace (server, pipeline, seeder, dashboard) conforms to the
 * event types and shapes defined here. Do NOT fork this schema elsewhere.
 *
 * PRIVACY INVARIANT: events carry lengths, shapes, reasons, and identities —
 * NEVER input values, phone numbers, OTP codes, or keystroke contents.
 *
 * Each event on the wire looks like: { t, e, ...payload }
 *   t = ms since page_load (integer)
 *   e = event type (one of EVENTS below)
 */

export const EVENTS = {
  // ---- Page / context (emitted once, first event of every session) ----
  PAGE_LOAD: 'page_load',     // { device, browser, os, viewport:[w,h], screen:[w,h], market, language, path, referrer, connection }
  DOM_SNAPSHOT: 'dom_snapshot', // { els:[{ sel, tag, type?, autocomplete?, inputmode?, role?, label?, step?, disabled?, hidden? }] } — structure only, NO values. Extracted server-side into the session's `dom` column (never kept in the events array).

  // ---- Interaction ----
  CLICK: 'click',             // { el, x, y, w, step, tag, ew, eh, vis, nth?, known } id + page-px pos + VIEWPORT width (w) + step + element size (ew/eh)/visibility (NO values)
  FOCUS: 'focus',             // { el }
  BLUR: 'blur',               // { el, dwell_ms }      dwell = time focused
  INPUT: 'input',             // { el, len }           value length only
  PASTE: 'paste',             // { el, len }           pasted length only
  INPUT_CLEARED: 'input_cleared', // { el, retypes }   field emptied after having content
  SCROLL: 'scroll',           // { depth }             max scroll depth %, 0-100

  // ---- Friction ----
  VALIDATION_ERR: 'validation_err', // { field, reason, msg? }  msg = the error element's visible text, scrubbed (scrubText)
  RAGE_CLICK: 'rage_click',   // { el, count, x, y, w, step, tag, ew, eh, vis, nth?, known } N rapid clicks same spot
  IDLE: 'idle',               // { ms }                gap with no activity
  REPEATED_SUBMIT: 'repeated_submit', // { el, count }
  DISABLED_CLICK: 'disabled_click',   // { el, x, y, w, step } click landed on a disabled control (CTA locked)

  // ---- Flow ----
  STEP_CHANGE: 'step_change', // { from, to }          step names from config
  TAB_HIDDEN: 'tab_hidden',   // {}                    visibilitychange -> hidden
  TAB_VISIBLE: 'tab_visible', // { away_ms }           returned after being hidden

  // ---- Technical ----
  API_CALL: 'api_call',       // { url, method, status, error_key, latency_ms }
  JS_ERROR: 'js_error',       // { msg }               message only, no stack values
  OTP_ATTEMPT: 'otp_attempt', // { n, result }         result: submit|invalid|valid

  // ---- Custom (the site calls BlackBox.track / BlackBox.convert) ----
  GOAL: 'goal',               // { name }  a named milestone, e.g. "signup_started". Name only — no properties, so no PII path.

  // ---- Outcome (final event, sent via sendBeacon) ----
  SESSION_END: 'session_end', // { converted, last_step, duration_ms, reason }
};

/**
 * Server-side PII backstop. The tracer only ever emits the fields below, but the
 * `/ingest` endpoint is public, so the SERVER re-enforces the privacy invariant:
 * for each event type it keeps ONLY these payload keys; an unknown event type is
 * stripped to `{ t, e }` (fail closed). This is the allowlist counterpart to the
 * EVENTS shapes above — keep the two in sync.
 */
export const ALLOWED_FIELDS = {
  page_load: ['device', 'browser', 'os', 'viewport', 'screen', 'market', 'language', 'path', 'referrer', 'connection'],
  dom_snapshot: ['els'], // extracted server-side into the `dom` column, never stored in the events array
  click: ['el', 'x', 'y', 'w', 'step', 'tag', 'ew', 'eh', 'vis', 'nth', 'known'],
  focus: ['el'],
  blur: ['el', 'dwell_ms'],
  input: ['el', 'len'],
  paste: ['el', 'len'],
  input_cleared: ['el', 'retypes'],
  scroll: ['depth'],
  validation_err: ['field', 'reason', 'msg'],
  rage_click: ['el', 'count', 'x', 'y', 'w', 'step', 'tag', 'ew', 'eh', 'vis', 'nth', 'known'],
  idle: ['ms'],
  repeated_submit: ['el', 'count'],
  disabled_click: ['el', 'x', 'y', 'w', 'step'],
  step_change: ['from', 'to'],
  tab_hidden: [],
  tab_visible: ['away_ms'],
  api_call: ['url', 'method', 'status', 'error_key', 'latency_ms'],
  js_error: ['msg'],
  otp_attempt: ['n', 'result'],
  session_end: ['converted', 'last_step', 'duration_ms', 'reason'],
  goal: ['name'],
};

/** Goal names are identifiers, not free text: [A-Za-z0-9 _.:-], ≤64 chars. */
export function goalName(s) {
  if (typeof s !== 'string') return undefined;
  const out = s.replace(/[^A-Za-z0-9 _.:-]/g, '').trim().slice(0, 64);
  return out || undefined;
}

/**
 * Strip incoming events to the allowlist. Keeps `{ t, e }` plus only the known
 * payload keys for that event type; drops unknown types' payloads entirely. Pure
 * (no deps) so it runs identically in the tracer build and on the server.
 */
/**
 * Page copy (e.g. a validation message) can echo what the user typed — "Number
 * 0712 345 678 is not eligible". Mask every digit run of 3+ and anything shaped
 * like an email, collapse whitespace, cap the length. Runs in the tracer AND
 * again in sanitizeEvents on the server, so an old or forged client can't
 * smuggle raw values through a free-text field.
 */
export const MSG_MAX = 140;
export function scrubText(s) {
  if (typeof s !== 'string') return undefined;
  const out = s
    .replace(/[^\s@]+@[^\s@]+\.[^\s@]+/g, '[email]')
    .replace(/\d[\d\s().+-]{1,}\d/g, (m) => (m.replace(/\D/g, '').length >= 3 ? '•••' : m))
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MSG_MAX);
  return out || undefined;
}

export function sanitizeEvents(events) {
  if (!Array.isArray(events)) return [];
  const out = [];
  for (const ev of events) {
    if (!ev || typeof ev !== 'object') continue;
    const clean = { t: typeof ev.t === 'number' ? ev.t : null, e: ev.e };
    const allowed = ALLOWED_FIELDS[ev.e];
    if (allowed) for (const k of allowed) if (ev[k] !== undefined) clean[k] = ev[k];
    if (clean.msg !== undefined && ev.e === 'validation_err') clean.msg = scrubText(clean.msg);
    if (ev.e === 'goal') { clean.name = goalName(clean.name); if (!clean.name) continue; }
    // unknown event type → only { t, e } survives (fail closed)
    out.push(clean);
  }
  return out;
}

export const STEPS = {
  LANDING: 'landing',
  PHONE: 'phone',
  OTP: 'otp',
  SUCCESS: 'success',
  COMING_SOON: 'coming_soon',
};

/**
 * Default config. Works out-of-the-box for universal events + the DOM snapshot
 * on ANY page. The per-page bits (steps, fields, errors, conversion) are empty
 * by default and come from a PRESET (see presets.js — selected via the script's
 * data-bb-preset attribute), from the dashboard's site config, or from BlackBox.init({...}).
 */
export const DEFAULT_CONFIG = {
  // Where traces are POSTed. Relative by default (same origin); set to an
  // absolute URL (or data-bb-endpoint) to collect from a third-party site.
  // NOTE: path is "/ingest", not "/collect" — ad blockers block "*/collect*"
  // (it's the Google Analytics path), which silently drops real users.
  endpoint: '/ingest',
  // Base URL for the read API used by REPLAY (GET /api/sessions/:id). Empty =
  // same origin; set to an absolute URL (or data-bb-api) for cross-origin replay.
  apiBase: '',
  // Public site-key sent with each batch (via data-bb-key). The collector checks
  // it only when BB_SITE_KEY is set server-side; null = no key (dev).
  siteKey: null,
  // Site label shown/filtered in the dashboard. Auto-derived from og:site_name
  // or the hostname; only set this (via init({site})) to force a custom label.
  site: null,

  // Console diagnostics (off by default; enable via ?bbdebug=true or init({debug:true})).
  debug: false,

  // Activation: recording is ON by default — one tag = recording. The dashboard's
  // site config can turn it off (`enabled: false`) or sample traffic (`sample` 0..1).
  // ?replay= and ?bbpreview=1 never record.
  enabled: true,
  sample: 1,

  // Batching.
  flushIntervalMs: 5000,
  flushMaxEvents: 20,
  // Keep-alive ping while the tab is VISIBLE (never while hidden) so a present-but-
  // idle visitor's dwell time is measured instead of recorded as 0. 0 = off.
  heartbeatMs: 20000,
  maxEvents: 5000,            // hard per-session safety cap — stop emitting past this (never storm)
  rateMaxPerSec: 80,          // circuit breaker — silently drop events past this/sec (page stays alive)
  idleThresholdMs: 8000,      // gap that counts as an idle/hesitation event
  // End (finalize) the session after this much VISIBLE inactivity — abandoned.
  // The clock pauses while the tab is hidden (reading the SMS is not abandoning),
  // and an idle-ended session resurrects if the user comes back — so a late
  // conversion is never miscounted as a drop. Server stale-sweep is the backstop.
  idleEndMs: 180000,
  rageWindowMs: 1000,         // window for counting rapid same-spot clicks
  rageThreshold: 3,

  // Our own first-party visitor id — generated + stored under this localStorage key,
  // so returning visitors link across sessions via OUR tag (no host dependency).
  visitorIdKey: 'bb_vid',

  // ---- Per-page (supplied by a preset or init()) ----
  // steps:  step container selectors — a step is "active" when NOT .hidden
  // fields: logical field name -> selector (labels input/validation)
  // errors: error elements watched for `.hidden` removal -> validation_err
  // conversion: { successSelector, apiSuccessUrl } — what counts as a conversion
  // Empty here so the tracer is page-agnostic; see presets.js for ro-nord etc.
  steps: {},
  fields: {},
  errors: {},
  conversion: {
    successSelector: null,
    apiSuccessUrl: null,
  },
};
